import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check, Copy, KeyRound, Loader2, Plus, ShieldCheck, Trash2, UserCog, UserX,
} from 'lucide-react'
import * as api from '../services/authApi'
import { useAuth, useChangePassword } from '../hooks/useAuth'
import { BFF_ORIGIN } from '../services/config'

// Settings → Accounts (plan-auth-layer.md A3). Three audiences in one panel:
// everyone changes their own password and manages their API tokens;
// administrators additionally manage accounts and read the auth event log.

function Section({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-parchment-border bg-white/70 dark:border-midnight-600 dark:bg-midnight-800/60">
      <div className="px-6 py-4 border-b border-parchment-border dark:border-midnight-600">
        <h2 className="font-display text-lg text-ink dark:text-mist">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-ink-soft dark:text-mist/60">{subtitle}</p>}
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </section>
  )
}

const inputCls =
  'w-full rounded-lg border border-parchment-border bg-white/80 px-3 py-2 text-sm text-ink outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 dark:border-midnight-600 dark:bg-midnight-900/60 dark:text-mist'
const btnCls =
  'inline-flex items-center gap-1.5 rounded-lg bg-gold px-3 py-2 text-sm font-medium text-midnight-900 hover:bg-gold-deep hover:text-white disabled:opacity-60'
const ghostBtnCls =
  'inline-flex items-center gap-1.5 rounded-md border border-parchment-border px-2.5 py-1.5 text-xs text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-50 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold'

function Err({ error }) {
  if (!error) return null
  return <div className="text-sm text-rose-600 dark:text-rose-400">{error.message}</div>
}

// ---------- own password ----------

function OwnPassword() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [done, setDone] = useState(false)
  const change = useChangePassword()

  return (
    <form
      className="space-y-3 max-w-sm"
      onSubmit={async (e) => {
        e.preventDefault()
        setDone(false)
        await change.mutateAsync({ current_password: current, new_password: next }).catch(() => {})
        if (!change.isError) {
          setCurrent(''); setNext(''); setDone(true)
        }
      }}
    >
      <input className={inputCls} type="password" placeholder="Current password" autoComplete="current-password"
        value={current} onChange={(e) => setCurrent(e.target.value)} required />
      <input className={inputCls} type="password" placeholder="New password (min 12 characters)" autoComplete="new-password"
        value={next} onChange={(e) => setNext(e.target.value)} required />
      <Err error={change.error} />
      {done && <div className="text-sm text-emerald-600 dark:text-emerald-400">Password changed.</div>}
      <button type="submit" className={btnCls} disabled={change.isPending}>
        {change.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Change password
      </button>
    </form>
  )
}

// ---------- API tokens ----------

function Tokens() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  // Held in component state only: the server returns the secret exactly once
  // and keeps a hash, so navigating away really does lose it.
  const [fresh, setFresh] = useState(null)
  const [copied, setCopied] = useState(false)
  const tokens = useQuery({ queryKey: ['auth', 'tokens'], queryFn: api.listTokens })
  const create = useMutation({
    mutationFn: api.createToken,
    onSuccess: (data) => { setFresh(data.token); setName(''); qc.invalidateQueries({ queryKey: ['auth', 'tokens'] }) },
  })
  const revoke = useMutation({
    mutationFn: api.revokeToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'tokens'] }),
  })

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-soft dark:text-mist/60">
        For clients that cannot sign in through a browser — the MCP facade, scripts, cron jobs.
        Send it as <code className="text-xs">Authorization: Bearer &lt;token&gt;</code>.
      </p>

      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); create.mutate(name || 'token') }}
      >
        <input className={inputCls} placeholder="What is it for? (e.g. claude-code)"
          value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className={btnCls} disabled={create.isPending}>
          {create.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
        </button>
      </form>
      <Err error={create.error} />

      {fresh && (
        <div className="rounded-lg border border-gold/50 bg-gold/10 p-3 space-y-2">
          <div className="text-xs font-medium text-gold-deep dark:text-gold">
            Copy this now — it is shown once and cannot be retrieved again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white/70 px-2 py-1 text-xs text-ink dark:bg-midnight-900/60 dark:text-mist">
              {fresh}
            </code>
            <button
              type="button"
              className={ghostBtnCls}
              onClick={() => { navigator.clipboard?.writeText(fresh); setCopied(true) }}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <details className="text-[11px] text-ink-soft dark:text-mist/50">
            <summary className="cursor-pointer">Register the MCP facade with it</summary>
            <code className="mt-1 block break-all">
              claude mcp add --transport http veralis {BFF_ORIGIN}/mcp --header "Authorization: Bearer {fresh}"
            </code>
          </details>
          <button type="button" className={ghostBtnCls} onClick={() => { setFresh(null); setCopied(false) }}>
            Done
          </button>
        </div>
      )}

      {tokens.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50">Loading…</div>}
      {tokens.data?.tokens?.length === 0 && (
        <div className="text-sm text-ink-soft dark:text-mist/50">No active tokens.</div>
      )}
      {!!tokens.data?.tokens?.length && (
        <ul className="divide-y divide-parchment-border dark:divide-midnight-600">
          {tokens.data.tokens.map((t) => (
            <li key={t.id} className="flex items-center gap-3 py-2">
              <KeyRound className="w-3.5 h-3.5 shrink-0 text-ink-soft dark:text-mist/50" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink dark:text-mist truncate">{t.name}</div>
                <div className="text-[11px] text-ink-soft dark:text-mist/50">
                  <code>{t.prefix}…</code> · created {String(t.created_at).slice(0, 10)}
                  {t.last_used_at ? ` · last used ${String(t.last_used_at).slice(0, 10)}` : ' · never used'}
                </div>
              </div>
              <button className={ghostBtnCls} onClick={() => revoke.mutate(t.id)} disabled={revoke.isPending}>
                <Trash2 className="w-3.5 h-3.5" /> Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------- accounts (admin) ----------

function Accounts() {
  const qc = useQueryClient()
  const { user: me } = useAuth()
  const [form, setForm] = useState({ username: '', password: '', role: 'user' })
  const users = useQuery({ queryKey: ['auth', 'users'], queryFn: api.listUsers })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['auth', 'users'] })
  const create = useMutation({ mutationFn: api.createUser, onSuccess: () => { setForm({ username: '', password: '', role: 'user' }); invalidate() } })
  const patch = useMutation({ mutationFn: ({ id, ...p }) => api.patchUser(id, p), onSuccess: invalidate })
  const reset = useMutation({ mutationFn: ({ id, password }) => api.resetUserPassword(id, password), onSuccess: invalidate })

  return (
    <div className="space-y-5">
      <form
        className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]"
        onSubmit={(e) => { e.preventDefault(); create.mutate(form) }}
      >
        <input className={inputCls} placeholder="Username" value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })} required />
        <input className={inputCls} type="password" placeholder="Temporary password (min 12)" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button type="submit" className={btnCls} disabled={create.isPending}>
          {create.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add
        </button>
      </form>
      <p className="text-[11px] text-ink-soft/70 dark:text-mist/40">
        The new account must change this password at first sign-in — a password you chose is a
        transport credential, not theirs.
      </p>
      <Err error={create.error} />
      <Err error={patch.error} />

      {users.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50">Loading…</div>}
      {!!users.data?.users?.length && (
        <ul className="divide-y divide-parchment-border dark:divide-midnight-600">
          {users.data.users.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm text-ink dark:text-mist">
                  {u.role === 'admin' && <ShieldCheck className="w-3.5 h-3.5 text-gold-deep dark:text-gold" />}
                  <span className="truncate">{u.display_name || u.username}</span>
                  {u.id === me?.id && <span className="text-[10px] text-ink-soft dark:text-mist/40">(you)</span>}
                  {!u.active && <span className="text-[10px] text-rose-600 dark:text-rose-400">disabled</span>}
                  {u.must_change_password && (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400">must change password</span>
                  )}
                </div>
                <div className="text-[11px] text-ink-soft dark:text-mist/50">
                  {u.username} · {u.role} · {u.last_login_at ? `last seen ${String(u.last_login_at).slice(0, 10)}` : 'never signed in'}
                </div>
              </div>
              <button
                className={ghostBtnCls}
                onClick={() => patch.mutate({ id: u.id, role: u.role === 'admin' ? 'user' : 'admin' })}
                disabled={patch.isPending}
              >
                <UserCog className="w-3.5 h-3.5" /> Make {u.role === 'admin' ? 'user' : 'admin'}
              </button>
              <button
                className={ghostBtnCls}
                onClick={() => patch.mutate({ id: u.id, active: !u.active })}
                disabled={patch.isPending}
              >
                <UserX className="w-3.5 h-3.5" /> {u.active ? 'Disable' : 'Enable'}
              </button>
              <button
                className={ghostBtnCls}
                onClick={() => {
                  const pw = window.prompt(`New temporary password for "${u.username}" (min 12 characters)`)
                  if (pw) reset.mutate({ id: u.id, password: pw })
                }}
                disabled={reset.isPending}
              >
                <KeyRound className="w-3.5 h-3.5" /> Reset password
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ink-soft/70 dark:text-mist/40">
        Disabling an account ends its sessions and revokes its tokens immediately. The last active
        administrator cannot be demoted or disabled.
      </p>
    </div>
  )
}

// ---------- audit (admin) ----------

const EVENT_TONE = {
  login_fail: 'text-rose-600 dark:text-rose-400',
  login_locked: 'text-rose-600 dark:text-rose-400',
  token_fail: 'text-rose-600 dark:text-rose-400',
  login_ok: 'text-emerald-600 dark:text-emerald-400',
}

function AuthEvents() {
  const events = useQuery({ queryKey: ['auth', 'events'], queryFn: () => api.listAuthEvents(100), refetchInterval: 60000 })
  if (events.isLoading) return <div className="text-sm text-ink-soft dark:text-mist/50">Loading…</div>
  if (!events.data?.events?.length) return <div className="text-sm text-ink-soft dark:text-mist/50">Nothing recorded yet.</div>
  return (
    <div className="max-h-80 overflow-y-auto">
      <table className="w-full text-[12px]">
        <tbody className="divide-y divide-parchment-border dark:divide-midnight-600">
          {events.data.events.map((e) => (
            <tr key={e.id} className="text-ink-soft dark:text-mist/60">
              <td className="py-1.5 pr-3 whitespace-nowrap font-mono text-[11px]">
                {String(e.ts).slice(0, 19).replace('T', ' ')}
              </td>
              <td className={`py-1.5 pr-3 whitespace-nowrap font-medium ${EVENT_TONE[e.event] || 'text-ink dark:text-mist'}`}>
                {e.event}
              </td>
              <td className="py-1.5 pr-3 truncate">{e.username || '—'}</td>
              <td className="py-1.5 pr-3 whitespace-nowrap font-mono text-[11px]">{e.ip || ''}</td>
              <td className="py-1.5 truncate" title={e.detail || ''}>{e.detail || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------- panel ----------

export default function AccountsPanel() {
  const { enabled, isAdmin } = useAuth()
  // With VERALIS_AUTH=off there are no accounts to manage; say so rather than
  // rendering controls whose every call would fail.
  if (!enabled) {
    return (
      <Section title="Accounts" subtitle="Authentication is disabled on this instance (VERALIS_AUTH=off).">
        <p className="text-sm text-ink-soft dark:text-mist/60">
          Every request is served unauthenticated. Turn auth on in <code>bff/.env</code> and restart
          to manage accounts and API tokens here.
        </p>
      </Section>
    )
  }
  return (
    <div className="space-y-6">
      <Section title="Your password" subtitle="Length beats punctuation — at least 12 characters.">
        <OwnPassword />
      </Section>
      <Section title="API tokens" subtitle="Non-interactive access for the MCP facade and scripts.">
        <Tokens />
      </Section>
      {isAdmin && (
        <>
          <Section title="Accounts" subtitle="Who can sign in. There is no self-service registration.">
            <Accounts />
          </Section>
          <Section title="Sign-in activity" subtitle="Logins, failures, lockouts and account changes.">
            <AuthEvents />
          </Section>
        </>
      )}
    </div>
  )
}
